import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import config from '../../../config';
import routeConfiguration from '../../../routeConfiguration';
import { neutral } from '../../../styles/mui/colors';
import { createResourceLocatorString } from '../../../util/routes';
import IconAustria from '../../Icons/Countries/IconAustria';
import IconDenmark from '../../Icons/Countries/IconDenmark';
import IconFrance from '../../Icons/Countries/IconFrance';
import IconGermany from '../../Icons/Countries/IconGermany';
import IconItaly from '../../Icons/Countries/IconItaly';
import IconSpain from '../../Icons/Countries/IconSpain';
import IconSweden from '../../Icons/Countries/IconSweden';
import IconSwiss from '../../Icons/Countries/IconSwiss';
import Link from '../../Links/Link/Link';

/**
 * Generates an array of country objects containing information needed to create flag buttons.
 * Each object includes an emoji for the country's flag, a label key for internationalized text,
 * an alt key for accessibility text, and parameters for generating a URL based on the current locale.
 *
 * @param {object} intl - The `intl` object from `react-intl`, used for locale-specific configurations.
 * @returns {Array<Object>} An array of country objects, each containing:
 *   - `emoji` {string}: The flag emoji representing the country.
 *   - `label` {string}: The message ID for the country's name, used with `FormattedMessage`.
 *   - `alt` {string}: The message ID for the alt text, used for accessibility.
 *   - `params` {Object}: The parameters for generating a URL, customized based on the locale.
 */
const countries = (intl) => [
  {
    icon: <IconAustria />,
    label: 'FlagButtons.Austria',
    alt: 'FlagButtons.Austria.alt',
    params: {
      ...(intl.locale === config.locale
        ? { slug: 'wohnmobilstellplatz', rest: 'oesterreich' }
        : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Österreich',
            bounds: '49.0205309,17.160776,46.372306,9.530748',
          }
        : {}),
    },
  },
  {
    icon: <IconSwiss />, // Sweden
    label: 'FlagButtons.Switzerland',
    alt: 'FlagButtons.Switzerland.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'schweiz' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Schweiz',
            bounds: '47.808454,10.492061,45.817986,5.955907',
          }
        : {}),
    },
  },
  {
    icon: <IconGermany />, // Sweden
    label: 'FlagButtons.Germany',
    alt: 'FlagButtons.Germany.alt',
    params: {
      ...(intl.locale === config.locale
        ? { slug: 'wohnmobilstellplatz', rest: 'deutschland' }
        : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Deutschland',
            bounds: '55.1145,15.04183,47.27023,5.86631',
          }
        : {}),
    },
  },
  {
    icon: <IconSpain />, // Sweden
    label: 'FlagButtons.Spain',
    alt: 'FlagButtons.Spain.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'spanien' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Spanien',
            bounds: '43.7913,4.3276,27.4333,-18.3937',
          }
        : {}),
    },
  },
  {
    icon: <IconFrance />, // Sweden
    label: 'FlagButtons.France',
    alt: 'FlagButtons.France.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'frankreich' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Frankreich',
            bounds: '51.1242,9.6625,41.3333,-5.5596',
          }
        : {}),
    },
  },
  {
    icon: <IconItaly />, // Sweden
    label: 'FlagButtons.Italy',
    alt: 'FlagButtons.Italy.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'italien' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Italien',
            bounds: '47.092,18.5204,35.288,6.6267',
          }
        : {}),
    },
  },
  {
    icon: <IconSweden />, // Sweden
    label: 'FlagButtons.Sweden',
    alt: 'FlagButtons.Sweden.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'schweden' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Schweden',
            bounds: '69.0599, 24.1933,55.2807,10.8364',
          }
        : {}),
    },
  },
  {
    icon: <IconDenmark />,
    label: 'FlagButtons.Denmark',
    alt: 'FlagButtons.Denmark.alt',
    params: {
      ...(intl.locale === config.locale ? { slug: 'wohnmobilstellplatz', rest: 'daenemark' } : {}),
    },
    search: {
      ...(intl.locale !== config.locale
        ? {
            address: 'Dänemark',
            bounds: '57.7533,15.0927,54.5701,8.0838',
          }
        : {}),
    },
  },
];

const boxStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
};

const flagButtonStyle = {
  fontWeight: 'bold',
  minHeight: 'unset',
  backgroundColor: '#fff',
  borderRadius: '50px',
  border: `1px solid ${neutral[200]}`,
};

/**
 * FlagButtonsComponent renders a list of buttons representing different countries.
 * Each button displays a country's flag and name, and navigates to a specific URL when clicked.
 * The URLs are generated based on the current locale and the selected country.
 *
 * @component
 *
 * @returns {JSX.Element} A component rendering a list of country flag buttons.
 */
const FlagButtonsComponent = () => {
  const intl = useIntl();

  return (
    <Box sx={boxStyle}>
      {countries(intl).map((country, index) => {
        const url = createResourceLocatorString(
          intl.locale === config.locale ? 'StoryblokPage' : 'SearchPage',
          routeConfiguration(),
          country.params,
          country.search,
          intl.locale
        );
        return (
          <Button
            color="inherit"
            component={Link}
            key={index}
            startIcon={country.icon}
            sx={flagButtonStyle}
            to={url}
            variant="contained"
          >
            <FormattedMessage id={country.label} />
          </Button>
        );
      })}
    </Box>
  );
};

FlagButtonsComponent.displayName = 'FlagButtons';

const FlagButtons = memo(FlagButtonsComponent);
export default FlagButtons;
